import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Gift, Users } from 'lucide-react';

export const AdminCreditManager = () => {
  const [email, setEmail] = useState('');
  const [credits, setCredits] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [batchEmails, setBatchEmails] = useState('');
  const [batchCredits, setBatchCredits] = useState('');
  const [batchReason, setBatchReason] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [grantToAll, setGrantToAll] = useState(false);
  const { toast } = useToast();

  const handleAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !credits || !reason) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    const creditAmount = parseInt(credits);
    if (isNaN(creditAmount) || creditAmount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Credits must be a positive number',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // Find user by email
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('email', email)
        .single();

      if (profileError || !profile) {
        toast({
          title: 'User Not Found',
          description: 'No user found with that email address',
          variant: 'destructive',
        });
        return;
      }

      // Call the manual credit adjustment function
      const { error: adjustError } = await supabase.rpc('manual_credit_adjustment', {
        p_user_id: profile.user_id,
        p_credit_change: creditAmount,
        p_adjustment_type: 'promotional',
        p_reason: reason,
      });

      if (adjustError) {
        console.error('Error adjusting credits:', adjustError);
        toast({
          title: 'Error',
          description: 'Failed to add credits. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Credits Added Successfully',
        description: `${creditAmount} credits added to ${email}`,
      });

      // Clear form
      setEmail('');
      setCredits('');
      setReason('');
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!batchCredits || !batchReason) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in credits and reason',
        variant: 'destructive',
      });
      return;
    }

    if (!grantToAll && !batchEmails) {
      toast({
        title: 'Missing Information',
        description: 'Please enter student emails or select "Grant to All Students"',
        variant: 'destructive',
      });
      return;
    }

    const creditAmount = parseInt(batchCredits);
    if (isNaN(creditAmount) || creditAmount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Credits must be a positive number',
        variant: 'destructive',
      });
      return;
    }

    setBatchLoading(true);
    try {
      let emailsToProcess: string[] = [];
      
      if (grantToAll) {
        // Get all student emails
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('email, user_id')
          .eq('role', 'student');

        if (profilesError) throw profilesError;
        emailsToProcess = profiles?.map(p => p.email) || [];
      } else {
        // Parse batch emails (comma or newline separated)
        emailsToProcess = batchEmails
          .split(/[,\n]/)
          .map(e => e.trim())
          .filter(e => e.length > 0);
      }

      if (emailsToProcess.length === 0) {
        toast({
          title: 'No Students Found',
          description: 'No valid student emails found',
          variant: 'destructive',
        });
        return;
      }

      let successCount = 0;
      let failCount = 0;

      // Process each email
      for (const email of emailsToProcess) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('email', email)
          .single();

        if (profileError || !profile) {
          failCount++;
          continue;
        }

        const { error: adjustError } = await supabase.rpc('manual_credit_adjustment', {
          p_user_id: profile.user_id,
          p_credit_change: creditAmount,
          p_adjustment_type: 'promotional',
          p_reason: batchReason,
        });

        if (adjustError) {
          failCount++;
        } else {
          successCount++;
        }
      }

      toast({
        title: 'Batch Credit Grant Complete',
        description: `${successCount} successful, ${failCount} failed out of ${emailsToProcess.length} students`,
      });

      // Clear form
      setBatchEmails('');
      setBatchCredits('');
      setBatchReason('');
      setGrantToAll(false);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setBatchLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5" />
          Manual Credit Grant
        </CardTitle>
        <CardDescription>
          Add credits to student accounts for promotions, compensations, or special events
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="single" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single">Single Student</TabsTrigger>
            <TabsTrigger value="batch">
              <Users className="h-4 w-4 mr-2" />
              Batch Grant
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="single">
            <form onSubmit={handleAddCredits} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Student Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="student@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="credits">Credits to Add</Label>
                <Input
                  id="credits"
                  type="number"
                  min="1"
                  placeholder="50"
                  value={credits}
                  onChange={(e) => setCredits(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Reason</Label>
                <Textarea
                  id="reason"
                  placeholder="Black Friday promotion, compensation for technical issue, etc."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={loading}
                  rows={3}
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Adding Credits...' : 'Add Credits'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="batch">
            <form onSubmit={handleBatchAddCredits} className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="grantToAll"
                    checked={grantToAll}
                    onChange={(e) => setGrantToAll(e.target.checked)}
                    disabled={batchLoading}
                    className="rounded border-input"
                  />
                  <Label htmlFor="grantToAll" className="text-sm font-medium">
                    Grant to ALL Students
                  </Label>
                </div>
              </div>

              {!grantToAll && (
                <div className="space-y-2">
                  <Label htmlFor="batchEmails">Student Emails (comma or newline separated)</Label>
                  <Textarea
                    id="batchEmails"
                    placeholder="student1@example.com, student2@example.com&#10;student3@example.com"
                    value={batchEmails}
                    onChange={(e) => setBatchEmails(e.target.value)}
                    disabled={batchLoading}
                    rows={5}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="batchCredits">Credits to Add (per student)</Label>
                <Input
                  id="batchCredits"
                  type="number"
                  min="1"
                  placeholder="50"
                  value={batchCredits}
                  onChange={(e) => setBatchCredits(e.target.value)}
                  disabled={batchLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="batchReason">Reason</Label>
                <Textarea
                  id="batchReason"
                  placeholder="Black Friday promotion, Holiday bonus, etc."
                  value={batchReason}
                  onChange={(e) => setBatchReason(e.target.value)}
                  disabled={batchLoading}
                  rows={3}
                />
              </div>

              <Button type="submit" disabled={batchLoading} className="w-full">
                {batchLoading ? 'Processing Batch...' : 'Grant Credits to Batch'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
